import React, { useState, useEffect } from "react";
import Navbar from "../widgets/Navbar";
import Sidebar from "../widgets/Sidebar";
import { useTranslation } from "react-i18next";
import {
  useAccountDetailsMutation,
  useGetAccountDetailsQuery,
  usePersonalDetailsMutation,
} from "../../redux/accountApi";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import toast from "react-hot-toast";

const Profile = () => {
  const { t } = useTranslation();
  const [formError, setFormError] = useState({ bank: {}, crypto: {}, email: {}, mobile: {} });
  const [formData, setFormData] = useState({
    firstName: "",
    accountNumber: "",
    bankName: "",
    ifscCode: "",
    cryptoWallet: "",
    email: "",
    mobile: "",
  });

  const [automateCountryCode, setAutomateCountryCode] = useState("us");
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [mobileDisabled, setMobileDisabled] = useState(false);
  const [isInformation, setIsInformation] = useState(false);
  const [isBankSubmitting, setIsBankSubmitting] = useState(false);
  const [isCryptoSubmitting, setIsCryptoSubmitting] = useState(false);

  const { data: userDetails, isLoading, error, refetch } = useGetAccountDetailsQuery();
  const [addAccountDetails] = useAccountDetailsMutation();
  const [addPersonalDetails, { isLoading: isSubmittingPersonal }] = usePersonalDetailsMutation();

  useEffect(() => {
    if (userDetails) {
      setFormData({
        firstName: userDetails.first_name || "",
        accountNumber: userDetails.account_number || "",
        bankName: userDetails.bank_name || "",
        ifscCode: userDetails.ifscCode || "",
        cryptoWallet: userDetails.crypto_wallet || "",
        email: userDetails.email || "",
        mobile: userDetails.mobile_number || "",
      });
      if (userDetails.email) setEmailDisabled(true);
      if (userDetails.mobile_number) setMobileDisabled(true);
      if (userDetails.email && userDetails.mobile_number) setIsInformation(true);
    }
  }, [userDetails]);

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    let formattedValue = value
    let errorMsg = "";

    switch (name) {
      case "firstName":
      case "bankName":
        formattedValue = formattedValue.replace(/\s+/g, " ").replace(/[^A-Za-z\s]/g, "").slice(0, 100);
        if (!formattedValue) errorMsg = t("This field is required");
        else if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(formattedValue)) errorMsg = t("End spaces not allowed");
        break;

      case "accountNumber":
        formattedValue = formattedValue.trim().replace(/\D/g, "").slice(0, 18);
        if (!formattedValue) errorMsg = t("This field is required");
        else if (formattedValue.length < 8 || formattedValue.length > 18) errorMsg = t("Must be 8 to 18 digits");
        break;

      case "ifscCode":
        formattedValue = formattedValue.trim().toUpperCase().replace(/\s+/g, "").replace(/[^A-Z0-9]/g, "").slice(0, 11);
        if (!formattedValue) errorMsg = t("This field is required");
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formattedValue)) errorMsg = t("Enter a valid IFSC code");
        break;

      case "cryptoWallet":
        formattedValue = formattedValue.trim().toUpperCase().replace(/\s+/g, "").replace(/[^A-Z0-9]/g, "").slice(0, 42);
        if (!formattedValue) errorMsg = t("This field is required");
        else if (formattedValue.length < 25 || formattedValue.length > 42) errorMsg = t("Wallet address must be 25-42 characters");
        break;

      case "email":
        if (!formattedValue) errorMsg = t("This field is required");
        else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formattedValue)) errorMsg = t("Invalid email format");
        break;

      default:
        break;
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    setFormError((prev) => ({
      ...prev,
      [formType]: {
        ...prev[formType],
        [name]: errorMsg,
      },
    }));
  };

  const handleInputChangeMobile = (value) => {
    setFormData((prev) => ({ ...prev, mobile: value }));
  };

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    const fieldList = formType === "bank" ? ["firstName", "accountNumber", "bankName", "ifscCode"] : ["cryptoWallet"];
    const currentErrors = {};

    fieldList.forEach((name) => {
      handleInputChange({ target: { name, value: formData[name] } }, formType);
      if (!formData[name] || formError[formType][name]) {
        currentErrors[name] = t("This field is required");
      }
    });
    if (Object.keys(currentErrors).length > 0) return;

    const payload = formType === "bank"
      ? { type: "bank", firstName: formData.firstName, accountNumber: formData.accountNumber, bankName: formData.bankName, ifscCode: formData.ifscCode }
      : { type: "crypto", cryptoWallet: formData.cryptoWallet };

    try {
      formType === "bank" ? setIsBankSubmitting(true) : setIsCryptoSubmitting(true);
      await addAccountDetails(payload).unwrap();
      toast.success(t("Details submitted successfully!"));
    } catch {
      toast.error(t("An error occurred. Please try again."));
    } finally {
      formType === "bank" ? setIsBankSubmitting(false) : setIsCryptoSubmitting(false);
    }
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    handleInputChange({ target: { name: "email", value: formData.email } }, "email");
    if (!formData.email || formError.email.email) return;
    if (!formData.mobile || formData.mobile.length < 8) {
      setFormError((prev) => ({ ...prev, mobile: { mobile: t("Enter a valid mobile number") } }));
      return;
    }

    const updateType = formData.email !== userDetails.email
      ? "email"
      : formData.mobile !== userDetails.mobile_number
      ? "mobile"
      : null;

    try {
      const response = await addPersonalDetails({ email: formData.email, mobile_number: formData.mobile, updateType }).unwrap();
      response?.status ? toast.success(t("Details submitted successfully!")) : toast.error(t(response.message));
      refetch();
    } catch {
      toast.error(t("An error occurred. Please try again."));
    }
  };

  return (
    <div>
      <Navbar />
      <section className="p70 pt-md-5 pb-md-5">
        <div className="container">
          <div className="row">
            <Sidebar />
            <div className="col-xl-9 col-lg-8">
              {isLoading && <p>{t("Loading...")}</p>}
              {error && (
                <p className="text-danger">
                  {t("Failed to load user details")}
                </p>
              )}
              <div className="p-3 bg2 br20 mb-4">
                <h5 className="mb-2">{t("Personal Information")}</h5>
                <form onSubmit={(e) => handleSubmitProfile(e)} noValidate>
                  <input type="hidden" name="type" value="bank" />
                  <div className="row">
                    <div className="col-md-6">
                      <label htmlFor="email">{t("Email Address")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange(e, "email")}
                        placeholder={t("Enter email")}
                        required
                        disabled={emailDisabled}
                      />
                      {formError.email.email && (
                        <p className="text-danger mt-1">
                          {formError.email.email}
                        </p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="mobile">{t("Mobile Number")}</label>
                      <PhoneInput
                        key={"phoneInput"}
                        country={automateCountryCode}
                        value={String(formData.mobile)}
                        onChange={(e) => handleInputChangeMobile(e, "mobile")}
                        className=""
                        placeholder="Enter mobile number"
                        countryCodeEditable={false}
                        enableSearch={true}
                        inputProps={{
                          autoFocus: true,
                          name: "mobile",
                        }}
                        disabled={mobileDisabled}
                      />

                      {formError.mobile.mobile && (
                        <p className="text-danger mt-1">
                          {formError.mobile.mobile}
                        </p>
                      )}
                    </div>
                  </div>
                  {isInformation ? (
                    <></>
                  ) : (
                    <div className="mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmittingPersonal}
                      >
                        {isSubmittingPersonal
                          ? t("Submitting...")
                          : t("Submit Personal Details")}
                      </button>
                    </div>
                  )}
                </form>
              </div>
              <div className="p-3 bg2 br20 mb-4">
                <h5 className="mb-2">{t("Banking Details")}</h5>
                <form onSubmit={(e) => handleSubmit(e, "bank")} noValidate>
                  <input type="hidden" name="type" value="bank" />

                  {/* Full Name & Account Number */}
                  <div className="row">
                    <div className="col-md-6">
                      <label htmlFor="firstName">
                        {t("Account Holder Name")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange(e, "bank")}
                        placeholder={t("Enter Account Holder Name")}
                        required
                      />
                      {formError.bank.firstName && (
                        <p className="text-danger mt-1">
                          {formError.bank.firstName}
                        </p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="accountNumber">
                        {t("Account Number")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="accountNumber"
                        name="accountNumber"
                        value={formData.accountNumber}
                        onChange={(e) => handleInputChange(e, "bank")}
                        placeholder={t("Enter Account Number")}
                        required
                      />
                      {formError.bank.accountNumber && (
                        <p className="text-danger mt-1">
                          {formError.bank.accountNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* IFSC Code & Bank Name */}
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <label htmlFor="ifscCode">{t("IFSC Code")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="ifscCode"
                        name="ifscCode"
                        value={formData.ifscCode}
                        onChange={(e) => handleInputChange(e, "bank")}
                        placeholder={t("Enter IFSC Code")}
                        required
                      />
                      {formError.bank.ifscCode && (
                        <p className="text-danger mt-1">
                          {formError.bank.ifscCode}
                        </p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="bankName">{t("Bank Name")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="bankName"
                        name="bankName"
                        value={formData.bankName}
                        onChange={(e) => handleInputChange(e, "bank")}
                        placeholder={t("Enter Bank Name")}
                        required
                      />
                      {formError.bank.bankName && (
                        <p className="text-danger mt-1">
                          {formError.bank.bankName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
            
                  <button
  type="submit"
  className="btn btn-primary"
  disabled={isBankSubmitting || Object.values(formError.bank).some((e) => e)}
>
  {isBankSubmitting ? t("Submitting...") : t("Submit Banking Details")}
</button>
                  </div>
                </form>
              </div>

              <div className="p-3 bg2 br20 mb-4">
                <h5 className="mb-2">{t("Crypto Wallet Address")}</h5>
                <form onSubmit={(e) => handleSubmit(e, "crypto")} noValidate>
                  <input type="hidden" name="type" value="crypto" />
                  <div className="row">
                    <div className="col-md-6">
                      <label htmlFor="cryptoWallet">
                        {t("Wallet Address")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="cryptoWallet"
                        name="cryptoWallet"
                        value={formData.cryptoWallet}
                        onChange={(e) => handleInputChange(e, "crypto")}
                        placeholder={t("Enter crypto wallet address")}
                        required
                      />
                      {formError.crypto.cryptoWallet && (
                        <p className="text-danger mt-1">
                          {formError.crypto.cryptoWallet}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
               
                  <button
  type="submit"
  className="btn btn-primary"
  disabled={isCryptoSubmitting || Object.values(formError.crypto).some((e) => e)}
>
  {isCryptoSubmitting ? t("Submitting...") : t("Submit Wallet Address")}
</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Profile;
